import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Box from '@mui/material/Box';
import { Text } from 'components/Text';

export interface CvTimelineStep {
  title: string;
  description?: string;
  /**
   * `done` — already happened (the submit itself).
   * `current` — happening now; gets the emphasised marker.
   * `upcoming` — still ahead; dimmed.
   */
  state: 'done' | 'current' | 'upcoming';
}

/**
 * An ordered "what happens next" list with a connecting rail.
 *
 * Every post-submit screen we benchmarked (Alan, Mercury, Marcus, Fidelity, Binance)
 * converges on this shape rather than a paragraph: the nodes make the process legible, and
 * the current node tells the user where their case actually sits.
 */
export const CvSuccessTimeline = ({ steps }: { steps: CvTimelineStep[] }) => (
  <Box
    data-testid="cv-success-timeline"
    sx={{ width: '100%', textAlign: 'left', mt: 2 }}
  >
    {steps.map((step, index) => {
      const isLast = index === steps.length - 1;
      const isUpcoming = step.state === 'upcoming';

      return (
        <Box key={step.title} sx={{ display: 'flex', gap: 1.5 }}>
          {/* Marker column: dot plus the rail down to the next node. */}
          <Box
            sx={{
              flex: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: 20
            }}
          >
            {step.state === 'done' ? (
              <CheckCircleIcon
                sx={{ fontSize: 18, color: 'success.main', mt: '2px' }}
              />
            ) : (
              <Box
                sx={theme => ({
                  width: 12,
                  height: 12,
                  mt: '5px',
                  borderRadius: '50%',
                  border: `2px solid ${
                    step.state === 'current'
                      ? theme.palette.primary.main
                      : theme.palette.divider
                  }`,
                  boxShadow:
                    step.state === 'current'
                      ? `0 0 0 3px ${theme.palette.primary.lighter}`
                      : 'none'
                })}
              />
            )}
            {!isLast && (
              <Box
                sx={theme => ({
                  flex: 1,
                  width: 1,
                  minHeight: 18,
                  mt: 0.5,
                  backgroundColor: theme.palette.divider
                })}
              />
            )}
          </Box>

          <Box sx={{ minWidth: 0, pb: isLast ? 0 : 2 }}>
            <Text
              variant="subtitle2"
              color={isUpcoming ? 'text.secondary' : 'text.primary'}
            >
              {step.title}
            </Text>
            {step.description && (
              <Text variant="body2" sx={{ mt: 0.25 }}>
                {step.description}
              </Text>
            )}
          </Box>
        </Box>
      );
    })}
  </Box>
);
